import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import type * as React from "react";

import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/components/primitives/pagination";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { ColocaIconButton } from "../buttons/ColocaIconButton";
import { Separator } from "../primitives/separator";

function getPageRange(page: number, totalPages: number): (number | "ellipsis")[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const windowStart = Math.max(2, page - 2);
  const windowEnd = Math.min(totalPages - 1, page + 2);
  const pages: (number | "ellipsis")[] = [1];
  if (windowStart > 2) pages.push("ellipsis");
  for (let i = windowStart; i <= windowEnd; i++) pages.push(i);
  if (windowEnd < totalPages - 1) pages.push("ellipsis");
  pages.push(totalPages);
  return pages;
}

export interface ColocaPaginationProps {
  /** Current page number (1-indexed). */
  page: number;
  /** Number of items per page. @default 25 */
  perPage?: number;
  /** Total number of items across all pages. */
  total: number;
  /**
   * Draw a rule above the pagination row. Defaults to `true` for the standalone case, where nothing else
   * marks the boundary between the list and its pagination.
   *
   * Pass `false` inside a `ColocaDataTable`'s `footer` slot: that slot already draws the rule and the
   * padding, so leaving this on renders the same line twice, 12px apart.
   */
  withSeparator?: boolean;
  size?: "default" | "sm";
  /** Called with the new page number when the user navigates. */
  onPageChange: (page: number) => void;
  /**
   * Replaces the default results summary. Pass a node when the wording has to be module-specific, or to add
   * controls like a page-size select. Omit it and the shared `global.pagination_records` wording is used.
   */
  summary?: React.ReactNode;
  className?: string;
}

/**
 * Full-featured pagination bar.
 *
 * Renders numbered page buttons on the right and a translated results summary on the left. The page window is
 * calculated automatically — first and last pages are always visible, up to two neighbours of the current
 * page are shown, and ellipsis fill the gaps.
 */
export function ColocaPagination({
  page,
  perPage = 25,
  total,
  withSeparator = true,
  size,
  onPageChange,
  summary,
  className,
}: ColocaPaginationProps) {
  // The fallback summary used to be hardcoded English, and three screens render it
  // (wallets, the wallet statement, the cards statement) because they pass no `summary`.
  const { t } = useTranslations("global");
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const from = total === 0 ? 0 : (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);
  const pageItems = getPageRange(page, totalPages);

  return (
    <>
      {withSeparator && <Separator className="mb-2" />}

      <div className={cn("flex flex-wrap items-center justify-between gap-4", className)}>
        <div className="text-sm text-muted-foreground">
          {summary ?? (total === 0 ? t("pagination_empty") : t("pagination_records", { from, to, total }))}
        </div>
        <Pagination className="mx-0 w-auto justify-start">
          <PaginationContent className="border border-sm rounded-md">
            <PaginationItem>
              <PaginationPrevious
                onClick={(e) => {
                  e.preventDefault();
                  if (page > 1) onPageChange(page - 1);
                }}
                size={size === "sm" ? "icon-sm" : undefined}
                aria-disabled={page <= 1}
                className={cn(page <= 1 && "pointer-events-none opacity-50")}
              />
            </PaginationItem>

            {pageItems.map((item, i) =>
              item === "ellipsis" ? (
                <PaginationItem key={`ellipsis-${i}`}>
                  <PaginationEllipsis size={size} />
                </PaginationItem>
              ) : (
                <PaginationItem key={item}>
                  <PaginationLink
                    size={size === "sm" ? "icon-sm" : undefined}
                    isActive={item === page}
                    onClick={(e) => {
                      e.preventDefault();
                      onPageChange(item);
                    }}
                  >
                    {item}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}

            <PaginationItem>
              <PaginationNext
                onClick={(e) => {
                  e.preventDefault();
                  if (page < totalPages) onPageChange(page + 1);
                }}
                size={size === "sm" ? "icon-sm" : undefined}
                aria-disabled={page >= totalPages}
                className={cn(page >= totalPages && "pointer-events-none opacity-50")}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </>
  );
}

/**
 * Minimal pagination control — previous and next icon buttons only.
 *
 * Use in dense layouts (table toolbars, mobile views) where a full page list would take too much space. For
 * full page navigation use `ColocaPagination`.
 */
export function ColocaPaginationSimple({
  page,
  perPage = 25,
  total,
  onPageChange,
  withSeparator = true,
  size,
  className,
}: ColocaPaginationProps) {
  const { t } = useTranslations("global");
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  return (
    <>
      {withSeparator && <Separator className="mb-2" />}
      <div className={cn("flex items-center justify-end", className)}>
        <div className="border rounded-md">
          <ColocaIconButton
            variant="ghost"
            icon={ChevronLeftIcon}
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            aria-label={t("pagination_prev_page")}
          />
          <ColocaIconButton
            variant="ghost"
            icon={ChevronRightIcon}
            size={size}
            disabled={page >= totalPages}
            onClick={() => onPageChange(page + 1)}
            aria-label={t("pagination_next_page")}
          />
        </div>
      </div>
    </>
  );
}
